import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Slider } from "../ui/slider";
import { Input } from "../ui/input";
import { readFileAsDataUrl, getCroppedImageBlob } from "../../lib/cropImage";
import { api, fileUrl } from "../../lib/api";
import { toast } from "sonner";

export default function TokenUploadCropModal({ open, onOpenChange, gameId, tag, onUploaded }) {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [name, setName] = useState("");
  const [uploading, setUploading] = useState(false);

  const onFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const dataUrl = await readFileAsDataUrl(file);
    setImageSrc(dataUrl);
    setName(file.name.split(".")[0]);
  };

  const onCropComplete = useCallback((_area, pixels) => setCroppedAreaPixels(pixels), []);

  const confirm = async () => {
    if (!imageSrc) return;
    if (!croppedAreaPixels) {
      toast.error("Still preparing the image — wait a second and try again");
      return;
    }
    setUploading(true);
    try {
      const blob = await getCroppedImageBlob(imageSrc, croppedAreaPixels);
      const formData = new FormData();
      formData.append("file", blob, `${name || "token"}.png`);
      const { data } = await api.post(`/uploads?game_id=${gameId}&kind=token&tag=${tag || "pc"}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded({ url: fileUrl(data.id), name: name || "Token" });
      onOpenChange(false);
      setImageSrc(null);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#322216] border-white/10 text-[#f1e0c5] max-w-lg" data-testid="token-upload-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Upload Your Token</DialogTitle>
        </DialogHeader>
        {!imageSrc ? (
          <Input data-testid="token-file-input" type="file" accept="image/*" onChange={onFileChange} className="bg-black/30 border-white/10" />
        ) : (
          <div className="space-y-4">
            <div className="relative w-full h-72 bg-black rounded-xl overflow-hidden">
              <Cropper image={imageSrc} crop={crop} zoom={zoom} aspect={1} cropShape="round" showGrid={false} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} />
            </div>
            <Slider data-testid="token-zoom-slider" value={[zoom]} min={1} max={3} step={0.05} onValueChange={([v]) => setZoom(v)} />
            <Input data-testid="token-name-input" placeholder="Token name" value={name} onChange={(e) => setName(e.target.value)} className="bg-black/30 border-white/10" />
            <Button data-testid="token-upload-confirm-button" onClick={confirm} disabled={uploading} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f]">
              {uploading ? "Uploading…" : "Save Token"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
